import { useTheme } from "next-themes";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  APPEARANCE_DEFAULTS,
  type Appearance,
  applyAppearance,
  LOSS_RATIO_DEFAULTS,
  type LossRatioAppearance,
  loadAppearance,
  saveAppearance,
} from "@/lib/appearance";
import type { StatusKey } from "@/lib/status-colors";

type AppearanceContextValue = {
  appearance: Appearance;
  setAccent: (value: string | null) => void;
  setBackground: (value: string | null) => void;
  setStatusColor: (key: StatusKey, value: string | null) => void;
  resetStatusColors: () => void;
  setLossRatio: (patch: Partial<LossRatioAppearance>) => void;
  resetLossRatio: () => void;
  reset: () => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const [appearance, setAppearance] = useState<Appearance>(APPEARANCE_DEFAULTS);
  // Watch the resolved theme so we can re-apply when the user toggles light /
  // dark — applyAppearance reads `<html class="dark">` to decide whether to
  // suspend the user's background pick, so it must run again on every flip.
  const { resolvedTheme } = useTheme();

  // Hydrate from localStorage once on mount.
  useEffect(() => {
    setAppearance(loadAppearance());
  }, []);

  // Re-apply on every appearance update OR theme flip so the body
  // background can be suspended (dark) or restored (light) to match.
  useEffect(() => {
    applyAppearance(appearance, resolvedTheme === "dark");
  }, [appearance, resolvedTheme]);

  const update = useCallback(
    (next: Appearance) => {
      setAppearance(next);
      saveAppearance(next);
      applyAppearance(next, resolvedTheme === "dark");
    },
    [resolvedTheme],
  );

  const setAccent = useCallback(
    (value: string | null) => {
      update({ ...appearance, accent: value });
    },
    [appearance, update],
  );

  const setBackground = useCallback(
    (value: string | null) => {
      update({ ...appearance, background: value });
    },
    [appearance, update],
  );

  // null clears the override and falls back to the status default.
  const setStatusColor = useCallback(
    (key: StatusKey, value: string | null) => {
      const next = { ...appearance.statusColors };
      if (value === null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      update({ ...appearance, statusColors: next });
    },
    [appearance, update],
  );

  const resetStatusColors = useCallback(() => {
    update({ ...appearance, statusColors: {} });
  }, [appearance, update]);

  const setLossRatio = useCallback(
    (patch: Partial<LossRatioAppearance>) => {
      update({ ...appearance, lossRatio: { ...appearance.lossRatio, ...patch } });
    },
    [appearance, update],
  );

  const resetLossRatio = useCallback(() => {
    update({ ...appearance, lossRatio: LOSS_RATIO_DEFAULTS });
  }, [appearance, update]);

  const reset = useCallback(() => {
    update(APPEARANCE_DEFAULTS);
  }, [update]);

  return (
    <AppearanceContext.Provider
      value={{
        appearance,
        setAccent,
        setBackground,
        setStatusColor,
        resetStatusColors,
        setLossRatio,
        resetLossRatio,
        reset,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error("useAppearance must be used inside <AppearanceProvider>");
  }
  return ctx;
}
